/**
 * Maps database/API errors to user-friendly messages.
 * Prevents leaking internal schema details via toast notifications.
 */
export function getUserFriendlyError(error: Error | any): string {
  const message = (error?.message ?? "").toLowerCase();
  const code = error?.code;

  // Postgres error codes
  switch (code) {
    case "23505": // unique_violation
      return "Este valor ya está en uso. Utiliza uno diferente.";
    case "23503": // foreign_key_violation
      return "No se puede completar la operación porque hay datos relacionados.";
    case "42501": // insufficient_privilege
    case "PGRST301": // RLS violation
      return "No tienes permisos para realizar esta acción.";
    case "23502": // not_null_violation
      return "Faltan campos obligatorios.";
  }

  // Pattern matching on message
  if (message.includes("duplicate key") || message.includes("unique constraint")) {
    return "Este valor ya está en uso. Utiliza uno diferente.";
  }
  if (message.includes("foreign key")) {
    return "No se puede completar la operación porque hay datos relacionados.";
  }
  if (message.includes("row-level security") || message.includes("insufficient_privilege")) {
    return "No tienes permisos para realizar esta acción.";
  }
  if (message.includes("not-null") || message.includes("null value")) {
    return "Faltan campos obligatorios.";
  }
  if (message.includes("network") || message.includes("fetch")) {
    return "Error de conexión. Comprueba tu conexión a internet.";
  }

  return "Ha ocurrido un error inesperado. Inténtalo de nuevo.";
}

/**
 * Validates a CSV venue row before database insert.
 * Returns null if valid, or an error message string.
 */
/**
 * Sanitizes a CSV cell value to prevent formula injection.
 * Strips leading characters that spreadsheet software interprets as formulas.
 */
export function sanitizeCsvCell(value: string): string {
  if (!value) return value;
  const dangerous = ['=', '@', '+', '-', '\t', '\r'];
  if (dangerous.some(char => value.startsWith(char))) {
    return value.replace(/^[=@+\-\t\r]+/, '');
  }
  return value;
}

export function validateVenueRow(row: Record<string, any>): string | null {
  if (!row.name || typeof row.name !== "string" || row.name.length > 200) {
    return "Nombre inválido o demasiado largo (máx. 200 caracteres)";
  }
  if (!row.city || typeof row.city !== "string" || row.city.length > 100) {
    return "Ciudad inválida o demasiado larga (máx. 100 caracteres)";
  }
  if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
    return "Email inválido";
  }
  if (row.website && !/^https?:\/\/.+/.test(row.website)) {
    return "URL de web inválida (debe empezar con http:// o https://)";
  }
  if (row.instagram && row.instagram.length > 200) {
    return "Instagram demasiado largo";
  }
  if (row.description && row.description.length > 2000) {
    return "Descripción demasiado larga (máx. 2000 caracteres)";
  }
  if (row.capacity != null && (isNaN(row.capacity) || row.capacity < 0 || row.capacity > 999999)) {
    return "Aforo inválido";
  }
  if (row.official_price != null && (isNaN(row.official_price) || row.official_price < 0)) {
    return "Precio inválido";
  }
  const validTypes = ["sala", "teatro", "auditorio", "exterior"];
  if (row.type && !validTypes.includes(row.type)) {
    return `Tipo inválido (valores válidos: ${validTypes.join(", ")})`;
  }
  return null;
}
